import { fieldRegistry } from '../../fields/registry/FieldRegistry';
import { getDomainProfile } from '../profileRegistry';

/**
 * Returns only aliases backed by a registered field manifest. Profile aliases
 * may also contain structural or compatibility-only names; those must not be
 * promoted as arbitrary folder-record properties because unregistered fields
 * have no reviewed persistence or protected-access classification.
 */
export function importableProfileFieldAliases(
  profileId: string,
): Readonly<Record<string, readonly string[]>> {
  const profile = getDomainProfile(profileId);
  if (!profile) return {};

  const registeredFieldIds = new Set(
    profile.importableFieldIds
      ?? fieldRegistry.getAllPacks().flatMap((pack) =>
        pack.fields.map((field) => field.id),
      ),
  );

  return Object.fromEntries(
    Object.entries(profile.fieldAliases)
      .filter(([fieldId]) => registeredFieldIds.has(fieldId))
      .map(([fieldId, aliases]) => [fieldId, [...aliases]]),
  );
}

/**
 * Entity refs (person, position, …) each registered field may be filed on,
 * limited to fields the profile can import. Empty when the profile does not
 * declare any, in which case every preserved field lands on the person.
 */
export function importableProfileFieldEntityRefs(
  profileId: string,
): Readonly<Record<string, readonly string[]>> {
  const profile = getDomainProfile(profileId);
  if (!profile?.importableFieldEntityRefs) return {};
  const importable = new Set(Object.keys(importableProfileFieldAliases(profileId)));
  return Object.fromEntries(
    Object.entries(profile.importableFieldEntityRefs)
      .filter(([fieldId]) => importable.has(fieldId))
      .map(([fieldId, refs]) => [fieldId, [...refs]]),
  );
}
